import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { fetchUserAccount, updateUserAccount, type UserAccount as UserAccountData } from '../api/userAccount';
import { fetchCoinbaseHoldings, updateCoinbaseHoldings, type CoinbaseHolding } from '../api/coinbaseHoldings';
import { getAllUsers, type User } from '../api/users';
import { TOKENS } from '@sepori/shared/registry';

// Helper function to get all tokens available for Coinbase Holdings (USD first, then registry tokens)
const getCoinbaseTokens = () => {
  const registryTokens = Object.values(TOKENS).sort((a, b) => a.display_order - b.display_order);
  return [
    { symbol: 'USD', display_order: 0 }, // USD appears first
    ...registryTokens
  ];
};

export const UserAccount: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [account, setAccount] = useState<UserAccountData | null>(null);
  const [formData, setFormData] = useState({
    total_invested_usd: 0,
    veaero_amount: 0,
    total_claimed_yield_aero: 0,
    bank_balance_usd: 0
  });
  // Store raw string values for number inputs to preserve decimal typing
  const [rawInputValues, setRawInputValues] = useState<Record<string, string>>({});
  // Store raw string values for Coinbase holdings amounts to preserve decimal precision
  const [rawCoinbaseAmounts, setRawCoinbaseAmounts] = useState<Record<number, string>>({});
  const [coinbaseHoldings, setCoinbaseHoldings] = useState<CoinbaseHolding[]>([]);
  const [showCopyHoldingsModal, setShowCopyHoldingsModal] = useState(false);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }

    if (!id) {
      navigate('/users');
      return;
    }

    const loadAccount = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetchUserAccount(parseInt(id, 10), true); // Pass true for admin access
        const accountData = response.account;
        
        setAccount(accountData);
        setFormData({
          total_invested_usd: Number(accountData.total_invested_usd) || 0,
          veaero_amount: Number((accountData as any).veaero_amount ?? 0) || 0,
          total_claimed_yield_aero: Number(accountData.total_claimed_yield_aero) || 0,
          bank_balance_usd: Number(accountData.bank_balance_usd) || 0
        });

        // Load coinbase holdings
        try {
          const authToken = localStorage.getItem('authToken') || '';
          const holdings = await fetchCoinbaseHoldings(parseInt(id, 10), authToken);
          // Convert amount to number to ensure proper type handling
          const formattedHoldings = holdings.map(h => ({
            ...h,
            amount: Number(h.amount)
          }));
          setCoinbaseHoldings(formattedHoldings);
          // Initialize raw amounts for decimal preservation
          const rawAmounts: Record<number, string> = {};
          formattedHoldings.forEach((h, idx) => {
            if (h.amount !== 0 && h.amount !== null && h.amount !== undefined) {
              rawAmounts[idx] = h.amount.toString();
            }
          });
          setRawCoinbaseAmounts(rawAmounts);
        } catch (err) {
          console.error('Failed to load coinbase holdings:', err);
        }
      } catch (err) {
        console.error('Failed to load user account:', err);
        setError(err instanceof Error ? err.message : 'Failed to load account data');
      } finally {
        setLoading(false);
      }
    };

    loadAccount();
  }, [id, isAdmin, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!id) return;

    try {
      setSaving(true);
      setError(null);
      
      // Validate and format form data to ensure proper numeric values
      const validatedFormData = {
        total_invested_usd: Number(formData.total_invested_usd.toFixed(2)),
        veaero_amount: Number(formData.veaero_amount.toFixed(2)),
        total_claimed_yield_aero: Number(formData.total_claimed_yield_aero.toFixed(2)),
        bank_balance_usd: Number(formData.bank_balance_usd.toFixed(2))
      };
      
      // Save investment tracking data
      await updateUserAccount(parseInt(id, 10), validatedFormData);
      
      // Save Coinbase holdings - preserve exact decimal precision typed by user
      const holdings = coinbaseHoldings.map((h, idx) => {
        // Use raw input value if available, otherwise use the stored amount
        const rawAmount = rawCoinbaseAmounts[idx];
        if (rawAmount !== undefined && rawAmount !== '') {
          // Parse the raw string to preserve exact decimal places
          return {
            token_symbol: h.token_symbol,
            amount: parseFloat(rawAmount) || 0
          };
        }
        // Fallback to stored amount (already a number)
        return {
        token_symbol: h.token_symbol,
          amount: h.amount
        };
      });

      const authToken = localStorage.getItem('authToken') || '';
      await updateCoinbaseHoldings(parseInt(id, 10), { holdings }, authToken);
      
      // Reload account data to get updated values
      const response = await fetchUserAccount(parseInt(id, 10), true);
      setAccount(response.account);
      
      alert('All changes saved successfully!');
    } catch (err) {
      console.error('Failed to save changes:', err);
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof typeof formData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const inputValue = e.target.value;
    
    // Allow empty string, numbers, and decimal points
    if (inputValue === '' || /^\d*\.?\d*$/.test(inputValue)) {
      // Limit to 2 decimal places for all fields
      const decimalIndex = inputValue.indexOf('.');
      let limitedValue = inputValue;
      if (decimalIndex !== -1) {
        const integerPart = inputValue.substring(0, decimalIndex);
        const decimalPart = inputValue.substring(decimalIndex + 1);
        // Limit decimal part to 2 digits
        limitedValue = decimalPart.length > 2 
          ? `${integerPart}.${decimalPart.substring(0, 2)}`
          : inputValue;
      }
      
      // Store raw string value to preserve decimal typing (e.g., "100.0" stays as "100.0")
      setRawInputValues(prev => ({ ...prev, [field]: limitedValue }));
      
      // Also update numeric value in formData for calculations
      if (limitedValue === '') {
        setFormData(prev => ({ ...prev, [field]: 0 }));
      } else {
        const numericValue = parseFloat(limitedValue);
      setFormData(prev => ({ ...prev, [field]: isNaN(numericValue) ? 0 : numericValue }));
      }
    }
    // If input doesn't match pattern, don't update (prevents invalid characters)
  };

  const handleInputBlur = (field: keyof typeof formData) => () => {
    // On blur, clear the raw input value so it uses the formatted numeric value
    setRawInputValues(prev => {
      const newValues = { ...prev };
      delete newValues[field];
      return newValues;
    });
  };

  const handleAddCoinbaseHolding = () => {
    // Find the first available token that's not already used (USD first, then registry tokens)
    const usedTokens = coinbaseHoldings.map(h => h.token_symbol);
    const availableTokens = getCoinbaseTokens()
      .filter(token => !usedTokens.includes(token.symbol));
    const firstAvailableToken = availableTokens.length > 0 ? availableTokens[0].symbol : 'USD';
    
    const newHolding: CoinbaseHolding = {
      id: Date.now(), // Temporary ID for new holdings
      user_id: parseInt(id!, 10),
      token_symbol: firstAvailableToken,
      amount: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    const newIndex = coinbaseHoldings.length;
    setCoinbaseHoldings(prev => [...prev, newHolding]);
    // Initialize empty raw amount for new holding
    setRawCoinbaseAmounts(prev => ({ ...prev, [newIndex]: '' }));
  };

  const handleUpdateCoinbaseHolding = (index: number, field: 'token_symbol' | 'amount', value: string | number) => {
    setCoinbaseHoldings(prev => prev.map((holding, i) => 
      i === index ? { ...holding, [field]: value } : holding
    ));
  };

  const handleRemoveCoinbaseHolding = (index: number) => {
    setCoinbaseHoldings(prev => {
      const filtered = prev.filter((_, i) => i !== index);
      // Rebuild rawCoinbaseAmounts with correct indices after removal
      setRawCoinbaseAmounts(prevRaw => {
        const newRawAmounts: Record<number, string> = {};
        filtered.forEach((_, newIndex) => {
          // Map new indices to old indices:
          // - Holdings before removed index: oldIndex = newIndex
          // - Holdings after removed index: oldIndex = newIndex + 1
          const oldIndex = newIndex < index ? newIndex : newIndex + 1;
          // Preserve the raw amount if it existed at the old index
          if (prevRaw[oldIndex] !== undefined) {
            newRawAmounts[newIndex] = prevRaw[oldIndex];
          }
        });
        return newRawAmounts;
      });
      return filtered;
    });
  };

  const handleCopyUserHoldings = async () => {
    if (!selectedUserId || !id) return;

    try {
      // Fetch the selected user's account data (investment tracking fields)
      const accountResponse = await fetchUserAccount(selectedUserId, true);
      const accountData = accountResponse.account;
      
      // Update form data with copied account values
      setFormData({
        total_invested_usd: Number(accountData.total_invested_usd) || 0,
        veaero_amount: Number((accountData as any).veaero_amount ?? 0) || 0,
        total_claimed_yield_aero: Number(accountData.total_claimed_yield_aero) || 0,
        bank_balance_usd: Number(accountData.bank_balance_usd) || 0
      });

      // Fetch the selected user's coinbase holdings
      const authToken = localStorage.getItem('authToken') || '';
      const holdings = await fetchCoinbaseHoldings(selectedUserId, authToken);
      
      // Convert amount to number and update state
      const copiedHoldings = holdings.map(h => ({
        ...h,
        amount: Number(h.amount),
        user_id: parseInt(id, 10) // Update to current user's ID
      }));
      
      setCoinbaseHoldings(copiedHoldings);
      // Initialize raw amounts for copied holdings
      const rawAmounts: Record<number, string> = {};
      copiedHoldings.forEach((h, idx) => {
        if (h.amount !== 0 && h.amount !== null && h.amount !== undefined) {
          rawAmounts[idx] = h.amount.toString();
        }
      });
      setRawCoinbaseAmounts(rawAmounts);
      setShowCopyHoldingsModal(false);
      setSelectedUserId(null);
    } catch (err) {
      console.error('Failed to copy user data:', err);
      setError(err instanceof Error ? err.message : 'Failed to copy user data');
    }
  };

  const loadAllUsers = async () => {
    try {
      const response = await getAllUsers();
      setAllUsers(response.users);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };


  if (!isAdmin) {
    return (
      <div className="user-account">
        <div className="error-container">
          <h2>Access Denied</h2>
          <p>Admin access required to manage user accounts.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="user-account">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading account data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-account">
        <div className="error-container">
          <h2>Error Loading Account</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="user-account">
      <header className="account-header">
        <div className="header-left">
          <h1>User Account Management</h1>
          <p>Manage investment tracking data for user ID: {id}</p>
          {account && (
            <p className="last-updated">
              Last updated: {new Date(account.updated_at).toLocaleString()}
            </p>
          )}
        </div>
        <div className="header-right">
          <button 
            onClick={() => navigate('/users')} 
            className="back-button"
          >
            ← Back to Users
          </button>
        </div>
      </header>

      <main className="account-content">
        <form onSubmit={handleSubmit} className="account-form">
          <div className="form-section">
            <h3>Investment Tracking</h3>
            <div className="form-group">
              <label htmlFor="total_invested_usd">
                Total Invested (USD)
              </label>
              <input
                type="number"
                step="0.01"
                id="total_invested_usd"
                value={rawInputValues.total_invested_usd !== undefined ? rawInputValues.total_invested_usd : formData.total_invested_usd.toString()}
                onChange={handleInputChange('total_invested_usd')}
                onBlur={handleInputBlur('total_invested_usd')}
                placeholder="0.00"
                required
              />
              <small>Total USD amount invested by this user</small>
            </div>

            <div className="form-group">
              <label htmlFor="veaero_amount">
                veAERO Amount
              </label>
              <input
                type="number"
                step="0.01"
                id="veaero_amount"
                value={rawInputValues.veaero_amount !== undefined ? rawInputValues.veaero_amount : formData.veaero_amount.toString()}
                onChange={handleInputChange('veaero_amount')}
                onBlur={handleInputBlur('veaero_amount')}
                placeholder="0.00"
                required
              />
              <small>Total veAERO amount for this user</small>
            </div>

            <div className="form-group">
              <label htmlFor="total_claimed_yield_aero">
                Total Claimed Yield (AERO)
              </label>
              <input
                type="number"
                step="0.01"
                id="total_claimed_yield_aero"
                value={rawInputValues.total_claimed_yield_aero !== undefined ? rawInputValues.total_claimed_yield_aero : formData.total_claimed_yield_aero.toString()}
                onChange={handleInputChange('total_claimed_yield_aero')}
                onBlur={handleInputBlur('total_claimed_yield_aero')}
                placeholder="0.00"
                required
              />
              <small>Total AERO tokens claimed as yield rewards</small>
            </div>

            <div className="form-group">
              <label htmlFor="bank_balance_usd">
                Bank Balance (USD)
              </label>
              <input
                type="number"
                step="0.01"
                id="bank_balance_usd"
                value={rawInputValues.bank_balance_usd !== undefined ? rawInputValues.bank_balance_usd : formData.bank_balance_usd.toString()}
                onChange={handleInputChange('bank_balance_usd')}
                onBlur={handleInputBlur('bank_balance_usd')}
                placeholder="0.00"
                required
              />
              <small>Any USD balance held in bank (rare)</small>
            </div>
          </div>

          <div className="form-section">
            <h3>Coinbase Holdings</h3>
            <p className="section-description">
              Track token balances held on Coinbase exchange. Current values will be calculated automatically.
            </p>

            <div className="coinbase-holdings-grid">
              <div className="holdings-header">
                <span>Token</span>
                <span>Amount</span>
                <span>Current Value</span>
                <span>Actions</span>
              </div>
              
              {coinbaseHoldings.map((holding, index) => (
                <div key={holding.id || index} className="holding-row">
                  <select
                    value={holding.token_symbol}
                    onChange={(e) => handleUpdateCoinbaseHolding(index, 'token_symbol', e.target.value)}
                    className="token-select"
                  >
                    {getCoinbaseTokens()
                      .filter(token => {
                        // Filter out tokens that are already used in other holdings
                        const isUsedElsewhere = coinbaseHoldings.some((h, i) => 
                          i !== index && h.token_symbol === token.symbol
                        );
                        return !isUsedElsewhere;
                      })
                      .map(token => (
                        <option key={token.symbol} value={token.symbol}>
                          {token.symbol}
                        </option>
                      ))}
                  </select>
                  
                  <input
                    type="number"
                    step="any"
                    value={rawCoinbaseAmounts[index] !== undefined ? rawCoinbaseAmounts[index] : (holding.amount || '')}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      
                      // Allow empty string, numbers, and decimal points (up to 8 decimal places)
                      if (inputValue === '' || /^\d*\.?\d*$/.test(inputValue)) {
                        // Limit to 8 decimal places
                        const decimalIndex = inputValue.indexOf('.');
                        let limitedValue = inputValue;
                        if (decimalIndex !== -1) {
                          const integerPart = inputValue.substring(0, decimalIndex);
                          const decimalPart = inputValue.substring(decimalIndex + 1);
                          // Limit decimal part to 8 digits
                          limitedValue = decimalPart.length > 8 
                            ? `${integerPart}.${decimalPart.substring(0, 8)}`
                            : inputValue;
                        }
                        
                        // Store raw string value to preserve decimal precision
                        setRawCoinbaseAmounts(prev => ({ ...prev, [index]: limitedValue }));
                        
                        // Also update numeric value for calculations
                        const numericValue = limitedValue === '' ? 0 : parseFloat(limitedValue);
                        handleUpdateCoinbaseHolding(index, 'amount', isNaN(numericValue) ? 0 : numericValue);
                      }
                    }}
                    onBlur={() => {
                      // On blur, keep the raw value but ensure we have a valid number
                      const rawValue = rawCoinbaseAmounts[index];
                      if (rawValue !== undefined && rawValue !== '') {
                        const numericValue = parseFloat(rawValue);
                        if (!isNaN(numericValue)) {
                      handleUpdateCoinbaseHolding(index, 'amount', numericValue);
                        }
                      }
                    }}
                    placeholder="0.00000000"
                    className="amount-input"
                  />
                  
                  <span className="current-value">
                    ${(holding.amount * 0).toFixed(2)} {/* TODO: Calculate with real prices */}
                  </span>
                  
                  <button
                    type="button"
                    onClick={() => handleRemoveCoinbaseHolding(index)}
                    className="remove-button"
                  >
                    Remove
                  </button>
                </div>
              ))}
              
              <div className="holdings-actions">
                <button
                  type="button"
                  onClick={handleAddCoinbaseHolding}
                  className="add-holding-button"
                >
                  + Add Token
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="form-error">
              <p>{error}</p>
            </div>
          )}

          <div className="form-actions">
            <button 
              type="button" 
              onClick={() => navigate('/users')}
              className="cancel-button"
            >
              Cancel
            </button>
            <button 
              type="button" 
              onClick={() => {
                loadAllUsers();
                setShowCopyHoldingsModal(true);
              }}
              className="copy-user-button"
            >
              📋 Copy User
            </button>
            <button 
              type="submit" 
              disabled={saving}
              className="save-button"
            >
              {saving ? (
                <>
                  <div className="spinner small"></div>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </main>

      {/* Copy User Holdings Modal */}
      {showCopyHoldingsModal && (
        <CopyUserHoldingsModal
          onClose={() => {
            setShowCopyHoldingsModal(false);
            setSelectedUserId(null);
          }}
          onCopy={handleCopyUserHoldings}
          allUsers={allUsers}
          selectedUserId={selectedUserId}
          onSelectUser={setSelectedUserId}
        />
      )}
    </div>
  );
};

interface CopyUserHoldingsModalProps {
  onClose: () => void;
  onCopy: () => void;
  allUsers: User[];
  selectedUserId: number | null;
  onSelectUser: (userId: number | null) => void;
}

const CopyUserHoldingsModal: React.FC<CopyUserHoldingsModalProps> = ({
  onClose,
  onCopy,
  allUsers,
  selectedUserId,
  onSelectUser
}) => {
  const selectedUser = allUsers.find(user => user.id === selectedUserId);
  
  return (
    <div className="modal-overlay">
      <div className="modal">
        <h3>Copy User Account Data</h3>
        <p>Select a user to copy their complete account data. This will replace ALL current data including investment tracking and coinbase holdings.</p>
        
        <div className="form-group">
          <label htmlFor="userSelect">Select User</label>
          <select
            id="userSelect"
            value={selectedUserId || ''}
            onChange={(e) => onSelectUser(e.target.value ? parseInt(e.target.value, 10) : null)}
            className="user-select"
          >
            <option value="">Choose a user...</option>
            {allUsers.map(user => (
              <option key={user.id} value={user.id}>
                {user.first_name} {user.last_name} ({user.email})
              </option>
            ))}
          </select>
        </div>

        {selectedUser && (
          <div className="confirmation-warning">
            <h4>⚠️ Warning</h4>
            <p>This will copy ALL account data from <strong>{selectedUser.first_name} {selectedUser.last_name}</strong> including:</p>
            <ul>
              <li>Total Invested (USD)</li>
              <li>Total Claimed Yield (AERO)</li>
              <li>Bank Balance (USD)</li>
              <li>All Coinbase Holdings</li>
            </ul>
            <p><strong>This action cannot be undone!</strong></p>
          </div>
        )}

        <div className="modal-actions">
          <button
            type="button"
            onClick={onClose}
            className="cancel-button"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onCopy}
            disabled={!selectedUserId}
            className="copy-button"
          >
            Copy All Data
          </button>
        </div>
      </div>
    </div>
  );
};
